import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { RefreshCcw, Search } from "lucide-react";
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DeviceBlock from "@/components/DeviceBlock";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { formatDeviceId } from "@/utils/deviceUtils";
import DeviceActivityChart from "@/components/DeviceActivityChart";
import { MotionAlerts } from "@/components/MotionAlerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MultiDeviceChart from "@/components/MultiDeviceChart";
import { SlaveControls } from "@/components/SlaveControls";
import { Seo } from "@/components/Seo";

export default function Index() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();
  
  // Use React Query for devices to leverage its caching and refetching
  const { data: devices, isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      console.log("Fetching all devices from Supabase...");
      const { data, error } = await supabase
        .from('devices')
        .select('device_id, name, last_seen, location, hardware_type, ip_address, signal')
        .order('name');
        
      if (error) {
        console.error("Error fetching devices:", error);
        throw error;
      }

      console.log(`Fetched ${data?.length || 0} devices from Supabase:`, data);
      
      // Log detailed device information for debugging
      data?.forEach(device => {
        console.log(`Device: ${device.name} (${device.device_id}) - Last seen: ${device.last_seen}`);
      });
      
      return data || [];
    },
    refetchInterval: 60000,
    staleTime: 30000,
    retry: 3
  });
  
  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    queryClient.invalidateQueries()
      .then(() => {
        toast.success("Data refreshed successfully");
      })
      .catch((error) => {
        toast.error("Failed to refresh data", {
          description: error.message || "Please try again"
        });
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  }, [queryClient]);

  // Filter devices based on search term
  const filteredDevices = devices?.filter(device => {
    const deviceName = device.name || `Device ${formatDeviceId(device.device_id, true)}`;
    return (
      deviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Separate master devices (for charts) from all devices
  const masterDevices = filteredDevices?.filter(device => 
    device.hardware_type !== 'ESP32 Slave'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Activity Dashboard · Perpetual Plexus"
        description="Monitor connected ESP32 masters and slaves, motion triggers, and live network health across the Perpetual Plexus installation."
        path="/"
        noindex
      />
      <Navigation />
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Perpetual Plexus Activity Dashboard</h1>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCcw className={isRefreshing ? "animate-spin" : ""} size={16} />
              Refresh
            </Button>
          </div>
        </div>
        
        <div className="flex gap-4 mb-6">
          <div className="ml-auto relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-[220px]"
            />
          </div>
        </div>

        {/* Web replacement for slave-device encoders */}
        <div className="mb-8">
          <SlaveControls />
        </div>

        {/* 24-hour activity chart at the top */}
        {masterDevices && masterDevices.length > 0 && (
          <div className="mb-8">
            <MultiDeviceChart 
              devices={masterDevices}
              chartType="line"
              days={0}
              title="24-Hour Activity Trends"
            />
          </div>
        )}

        {/* Multi-device charts section - only 7-day line chart */}
        {masterDevices && masterDevices.length > 1 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">7-Day Activity Overview</h2>
            <MultiDeviceChart 
              devices={masterDevices}
              chartType="line"
              days={7}
              title="Weekly Activity Trends"
            />
          </div>
        )}
        
        {/* Loading state */}
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading devices...
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">
            Error loading devices. Please try refreshing.
          </div>
        ) : filteredDevices?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {searchTerm ? "No devices match your search" : "No devices found"}
          </div>
        ) : (
          <>
            {/* Individual charts section - only for master devices */}
            {masterDevices && masterDevices.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Individual Device Activity</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {masterDevices.map((device) => (
                    <Card key={device.device_id} className="overflow-hidden">
                      <CardHeader className="bg-slate-50">
                        <CardTitle>{device.name || `Device ${formatDeviceId(device.device_id, true)}`}</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        {/* Device Activity Charts - Daily and Hourly */}
                        <div className="space-y-6 mb-6">
                          <DeviceActivityChart 
                            key={`daily-chart-${device.device_id}`}
                            deviceId={device.device_id}
                            title={`Daily Motion Activity: ${device.name || formatDeviceId(device.device_id, true)}`}
                            days={7}
                          />
                          
                          <DeviceActivityChart 
                            key={`hourly-chart-${device.device_id}`}
                            deviceId={device.device_id}
                            title={`Hourly Motion Activity: ${device.name || formatDeviceId(device.device_id, true)}`}
                            days={0}
                          />
                        </div>
                        
                        {/* Motion Alerts for this device */}
                        <MotionAlerts deviceId={device.device_id} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* All devices section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">All Devices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDevices.map((device) => (
                  <DeviceBlock 
                    key={device.device_id}
                    deviceId={device.device_id} 
                    deviceName={device.name || `Device ${formatDeviceId(device.device_id, true)}`}
                  />
                ))}
              </div>
            </div>
          </>
        )}
        
        {/* Enhanced debug info */}
        <div className="mt-8 p-3 bg-gray-100 rounded-md text-xs text-gray-500">
          <div className="font-semibold mb-1">Device Status Summary:</div>
          <ul className="space-y-1">
            {devices?.map(device => {
              const isOnline = device.last_seen 
                ? (new Date().getTime() - new Date(device.last_seen).getTime() < 300000) // 5 minutes
                : false;
              const deviceType = device.hardware_type === 'ESP32 Slave' ? 'SLAVE' : 'MASTER';
              return (
                <li key={device.device_id} className="font-mono flex justify-between">
                  <span>
                    {device.name || formatDeviceId(device.device_id, true)}: {device.device_id} ({deviceType})
                  </span>
                  <span className={isOnline ? "text-green-600" : "text-red-600"}>
                    {isOnline ? "ONLINE" : "OFFLINE"}
                    {device.last_seen ? ` (${new Date(device.last_seen).toLocaleString()})` : ' (Never seen)'}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2">
            Total devices: {devices?.length || 0} | 
            Online: {devices?.filter(d => d.last_seen && (new Date().getTime() - new Date(d.last_seen).getTime() < 300000)).length || 0} |
            Masters: {devices?.filter(d => d.hardware_type !== 'ESP32 Slave').length || 0} |
            Slaves: {devices?.filter(d => d.hardware_type === 'ESP32 Slave').length || 0}
          </p>
        </div>
      </div>
    </div>
  );
}
